import React, { useState, useRef, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledAlert } from 'reactstrap';
import { toast } from 'react-toastify';
import { ChatInputProps } from '../interfaces';
import QuickRepliesModal from '../QuickRepliesModal';
import { MessageReplyPreview } from './MediaAttachments';
import { AttachmentFile, AttachmentType } from '../../types/whatsapp';

// Límites oficiales de WhatsApp Cloud API
const MAX_FILE_SIZE_LIMITS: Record<AttachmentType, { bytes: number; label: string }> = {
    image: { bytes: 5 * 1024 * 1024, label: '5 MB' },
    video: { bytes: 16 * 1024 * 1024, label: '16 MB' },
    audio: { bytes: 16 * 1024 * 1024, label: '16 MB' },
    document: { bytes: 100 * 1024 * 1024, label: '100 MB' }
};

// Helper: Formato de tamaño de archivo (KB / MB)
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper: Obtener icono según el tipo o extensión del archivo
const getFileIconClass = (mimeType: string, filename: string): { icon: string; color: string } => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    if (mimeType.includes('pdf') || ext === 'pdf') {
        return { icon: 'ri-file-pdf-fill', color: 'text-danger' };
    }
    if (mimeType.includes('word') || ['doc', 'docx'].includes(ext)) {
        return { icon: 'ri-file-word-fill', color: 'text-primary' };
    }
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext)) {
        return { icon: 'ri-file-excel-fill', color: 'text-success' };
    }
    if (mimeType.includes('presentation') || ['ppt', 'pptx'].includes(ext)) {
        return { icon: 'ri-file-ppt-fill', color: 'text-warning' };
    }
    if (mimeType.includes('zip') || mimeType.includes('rar') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
        return { icon: 'ri-file-zip-fill', color: 'text-secondary' };
    }
    if (mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) {
        return { icon: 'ri-file-music-fill', color: 'text-info' };
    }
    if (mimeType.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv'].includes(ext)) {
        return { icon: 'ri-video-fill', color: 'text-primary' };
    }
    return { icon: 'ri-file-text-fill', color: 'text-primary' };
};

// Detección de comandos /
const getLastSlashCommand = (text: string): { hasCommand: boolean; command: string; afterSlash: string } => {
    const lastSlashIndex = text.lastIndexOf('/');
    if (lastSlashIndex === -1) {
        return { hasCommand: false, command: '', afterSlash: '' };
    }
    const beforeSlash = text.substring(0, lastSlashIndex);
    const afterSlash = text.substring(lastSlashIndex + 1);

    if (afterSlash.startsWith(' ')) {
        return { hasCommand: false, command: '', afterSlash: '' };
    }

    const urlPatterns = ['http://', 'https://', 'www.', '.com', '.org', '.net', '.io'];
    const isLikelyUrl = urlPatterns.some(pattern => beforeSlash.includes(pattern));

    if (isLikelyUrl) {
        return { hasCommand: false, command: '', afterSlash: '' };
    }

    return {
        hasCommand: true,
        command: '/' + afterSlash,
        afterSlash: afterSlash
    };
};

const ChatInput: React.FC<ChatInputProps> = ({
    replyingTo,
    setReplyingTo,
    isInputDisabled,
    selectedContact,
    inputRef,
    onSendMessage
}) => {
    const [messageText, setMessageText] = useState('');
    const [quickReplySearch, setQuickReplySearch] = useState('');
    const [showQuickReplies, setShowQuickReplies] = useState(false);

    // Estados para adjuntos y errores de tamaño
    const [selectedAttachment, setSelectedAttachment] = useState<AttachmentFile | null>(null);
    const [sizeError, setSizeError] = useState<string | null>(null);
    const [attachMenuOpen, setAttachMenuOpen] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [acceptFilter, setAcceptFilter] = useState('*/*');

    // Limpieza de ObjectURL en desmontaje o cambio de archivo
    useEffect(() => {
        return () => {
            if (selectedAttachment?.previewUrl) {
                URL.revokeObjectURL(selectedAttachment.previewUrl);
            }
        };
    }, [selectedAttachment]);

    const toggleAttachMenu = () => setAttachMenuOpen(prev => !prev);

    const adjustInputHeightAndFocus = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
            inputRef.current.focus();
        }
    }, [inputRef]);

    React.useEffect(() => {
        if (selectedContact && !isInputDisabled && inputRef.current) {
            const timeoutId = setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [selectedContact, isInputDisabled, inputRef]);

    // Procesar archivo seleccionado con validación de peso máximo
    const processFile = useCallback((file: File) => {
        const primaryType = file.type.split('/')[0]?.toLowerCase();
        let type: AttachmentType;
        let previewUrl: string | null = null;

        switch (primaryType) {
            case 'image':
                type = 'image';
                previewUrl = URL.createObjectURL(file);
                break;
            case 'video':
                type = 'video';
                previewUrl = URL.createObjectURL(file);
                break;
            case 'audio':
                type = 'audio';
                break;
            default:
                type = 'document';
                break;
        }

        const limit = MAX_FILE_SIZE_LIMITS[type];
        if (file.size > limit.bytes) {
            const typeLabel = type === 'image' ? 'imágenes' : type === 'video' ? 'videos' : type === 'audio' ? 'audios' : 'documentos';
            const errorMsg = `El archivo "${file.name}" (${formatFileSize(file.size)}) supera el límite máximo permitido por WhatsApp para ${typeLabel} (${limit.label}).`;
            setSizeError(errorMsg);
            toast.error(errorMsg);
            setSelectedAttachment(null);
            return;
        }

        setSizeError(null);
        setSelectedAttachment({
            file,
            previewUrl,
            type,
            name: file.name,
            size: file.size,
            mimeType: file.type
        });

        setTimeout(() => adjustInputHeightAndFocus(), 50);
    }, [adjustInputHeightAndFocus]);

    // Manejar selección por input de archivo
    const openFilePicker = (accept: string) => {
        setAcceptFilter(accept);
        setAttachMenuOpen(false);
        setTimeout(() => {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
                fileInputRef.current.click();
            }
        }, 100);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    };

    // Manejar pegado desde portapapeles (Cmd+V / Ctrl+V)
    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) {
                    e.preventDefault();
                    processFile(file);
                    break;
                }
            }
        }
    };

    // Manejar Drag & Drop
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isInputDisabled && !isDraggingOver) {
            setIsDraggingOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) {
            return;
        }
        setIsDraggingOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        if (isInputDisabled) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    };

    const handleRemoveAttachment = () => {
        if (selectedAttachment?.previewUrl) {
            URL.revokeObjectURL(selectedAttachment.previewUrl);
        }
        setSelectedAttachment(null);
        setSizeError(null);
    };

    const handleSendMessage = () => {
        if (selectedContact?.botActive) return;
        if (messageText.trim() || selectedAttachment) {
            onSendMessage(
                messageText.trim(),
                replyingTo?.id,
                selectedAttachment ? selectedAttachment.file : undefined
            );
            setMessageText('');
            handleRemoveAttachment();
            setReplyingTo(null);
            adjustInputHeightAndFocus();
        }
    };

    const handleQuickReplySelect = useCallback((response: string) => {
        const lastSlashIndex = messageText.lastIndexOf('/');
        if (lastSlashIndex !== -1) {
            const textBeforeSlash = messageText.substring(0, lastSlashIndex);
            const newText = textBeforeSlash + response;
            setMessageText(newText);

            setTimeout(() => {
                if (inputRef.current) {
                    adjustInputHeightAndFocus();
                    const startPos = textBeforeSlash.length;
                    const endPos = textBeforeSlash.length + response.length;
                    inputRef.current.setSelectionRange(startPos, endPos);
                }
            }, 0);
        } else {
            setMessageText(response);

            setTimeout(() => {
                if (inputRef.current) {
                    adjustInputHeightAndFocus();
                    inputRef.current.select();
                }
            }, 0);
        }
        setShowQuickReplies(false);
    }, [messageText, inputRef, adjustInputHeightAndFocus]);

    const handleQuickReplyClose = useCallback(() => {
        setShowQuickReplies(false);
    }, []);

    const handleQuickReplyForceClose = useCallback(() => {
        setShowQuickReplies(false);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setMessageText(newValue);

        const { hasCommand, afterSlash } = getLastSlashCommand(newValue);
        setShowQuickReplies(hasCommand);
        setQuickReplySearch(afterSlash);

        const target = e.target;
        target.style.height = 'auto';
        target.style.height = `${target.scrollHeight}px`;
    };

    const handleKeyDownWrapper = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            if (showQuickReplies) return;
            e.preventDefault();
            handleSendMessage();
        }
    };

    const isSendDisabled = (!messageText.trim() && !selectedAttachment) || isInputDisabled;

    return (
        <div
            className={classnames("chat-input-section p-3 p-lg-4 border-top flex-shrink-0 position-relative", {
                "border-primary bg-soft-primary": isDraggingOver
            })}
            style={{ background: 'var(--vz-card-bg)', transition: 'all 0.2s ease' }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Input de archivo oculto */}
            <input
                type="file"
                ref={fileInputRef}
                className="d-none"
                accept={acceptFilter}
                onChange={handleFileInputChange}
            />

            {/* Overlay visual para Drag & Drop */}
            {isDraggingOver && (
                <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-primary bg-opacity-10 border border-2 border-primary border-dashed rounded"
                    style={{ zIndex: 10, backdropFilter: 'blur(2px)', pointerEvents: 'none' }}
                >
                    <i className="ri-upload-cloud-2-line display-4 text-primary mb-1"></i>
                    <span className="fw-semibold text-primary fs-14">Suelta el archivo aquí para adjuntarlo</span>
                </div>
            )}

            {/* --- ALERTA DE ERROR DE TAMAÑO MÁXIMO EXCEDIDO --- */}
            {sizeError && (
                <UncontrolledAlert color="danger" className="mb-2 fs-12 border-0 shadow-sm d-flex align-items-center py-2 px-3">
                    <i className="ri-error-warning-fill me-2 fs-16 flex-shrink-0"></i>
                    <span className="flex-grow-1">{sizeError}</span>
                </UncontrolledAlert>
            )}

            {/* --- BLOQUE DE RESPUESTA --- */}
            {replyingTo && (
                <div className="row g-0 mb-2">
                    <div className="col" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center p-2 bg-light border-start border-3 border-primary rounded shadow-sm">
                            <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                                <div className="text-primary fw-semibold fs-12 mb-0">
                                    Responder a {replyingTo.direction === 'inbound' ? selectedContact?.name : 'ti'}
                                </div>
                                <div className="text-muted fs-13 text-truncate" style={{ opacity: 0.85, maxWidth: '100%' }}>
                                    <MessageReplyPreview message={replyingTo as any} />
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-muted ms-2 p-0"
                                onClick={() => setReplyingTo(null)}
                            >
                                <i className="ri-close-line fs-18"></i>
                            </button>
                        </div>
                    </div>
                    <div className="col-auto ms-2" style={{ width: '48px', flexShrink: 0 }}></div>
                </div>
            )}

            {/* --- BLOQUE DE ARCHIVO ADJUNTO SELECCIONADO --- */}
            {selectedAttachment && (
                <div className="row g-0 mb-2">
                    <div className="col" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center p-2 bg-white border border-primary border-opacity-25 rounded shadow-sm">
                            {/* Previsualización de Imagen/Video */}
                            {selectedAttachment.type === 'image' && selectedAttachment.previewUrl ? (
                                <div className="flex-shrink-0 me-3">
                                    <img
                                        src={selectedAttachment.previewUrl}
                                        alt="Preview"
                                        className="rounded object-fit-cover"
                                        style={{ width: '48px', height: '48px' }}
                                    />
                                </div>
                            ) : selectedAttachment.type === 'video' && selectedAttachment.previewUrl ? (
                                <div className="flex-shrink-0 me-3 position-relative">
                                    <video
                                        src={selectedAttachment.previewUrl}
                                        className="rounded object-fit-cover"
                                        style={{ width: '48px', height: '48px' }}
                                    />
                                    <i className="ri-play-circle-fill position-absolute top-50 start-50 translate-middle text-white fs-18"></i>
                                </div>
                            ) : (
                                /* Previsualización de Documento / Audio */
                                <div className="flex-shrink-0 me-3">
                                    <div
                                        className="avatar-xs d-flex align-items-center justify-content-center bg-light rounded"
                                        style={{ width: '44px', height: '44px' }}
                                    >
                                        <i className={classnames(
                                            getFileIconClass(selectedAttachment.mimeType, selectedAttachment.name).icon,
                                            getFileIconClass(selectedAttachment.mimeType, selectedAttachment.name).color,
                                            'fs-24'
                                        )}></i>
                                    </div>
                                </div>
                            )}

                            {/* Info de archivo e Indicador de Tamaño */}
                            <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-dark fs-13 text-truncate mb-0">
                                    {selectedAttachment.name}
                                </div>
                                <div className="text-muted fs-11 d-flex align-items-center gap-2">
                                    <span>{formatFileSize(selectedAttachment.size)}</span>
                                </div>
                            </div>

                            {/* Botón quitar adjunto */}
                            <button
                                type="button"
                                className="btn btn-sm btn-soft-danger rounded-circle p-1 ms-2"
                                onClick={handleRemoveAttachment}
                                title="Quitar archivo"
                            >
                                <i className="ri-close-line fs-16"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- BLOQUE DE INPUT PRINCIPAL --- */}
            <div className="row g-0 align-items-end">
                {/* Botón Adjuntar Archivo (📎 Clip Dropdown con Indicadores de Límite) */}
                <div className="col-auto me-2">
                    <Dropdown isOpen={attachMenuOpen} toggle={toggleAttachMenu} direction="up">
                        <DropdownToggle
                            tag="button"
                            type="button"
                            className="btn btn-link text-muted fs-20 p-0 shadow-none d-flex align-items-center justify-content-center"
                            style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                opacity: isInputDisabled ? 0.5 : 1,
                                cursor: isInputDisabled ? 'not-allowed' : 'pointer'
                            }}
                            disabled={isInputDisabled}
                            title="Adjuntar archivo o documento"
                        >
                            <i className="ri-attachment-2"></i>
                        </DropdownToggle>
                        <DropdownMenu className="shadow-lg border-0 rounded-3 p-2" style={{ minWidth: '240px' }}>
                            <DropdownItem
                                onClick={() => openFilePicker('image/*,video/*')}
                                className="d-flex align-items-center justify-content-between py-2 px-3 rounded"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-image-2-fill fs-18 text-primary"></i>
                                    <span>Fotos y Videos</span>
                                </div>
                            </DropdownItem>

                            <DropdownItem
                                onClick={() => openFilePicker('.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar')}
                                className="d-flex align-items-center justify-content-between py-2 px-3 rounded"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-file-text-fill fs-18 text-info"></i>
                                    <span>Documentos</span>
                                </div>
                            </DropdownItem>

                            <DropdownItem
                                onClick={() => openFilePicker('audio/*')}
                                className="d-flex align-items-center justify-content-between py-2 px-3 rounded"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-volume-up-fill fs-18 text-warning"></i>
                                    <span>Audio / Música</span>
                                </div>
                            </DropdownItem>

                            <DropdownItem divider className="my-1" />

                        </DropdownMenu>
                    </Dropdown>
                </div>

                {/* Caja de Texto */}
                <div className="col">
                    <textarea
                        ref={inputRef}
                        id="whatsapp-message-input"
                        className={classnames("form-control chat-input border-0 shadow-sm px-3", {
                            "bg-white": !isInputDisabled,
                            "bg-light text-muted": isInputDisabled
                        })}
                        style={{
                            padding: '0.90rem 1.25rem',
                            cursor: isInputDisabled ? 'not-allowed' : 'text',
                            resize: 'none',
                            minHeight: '48px',
                            maxHeight: '120px',
                            overflowY: 'auto',
                            borderRadius: '24px',
                            lineHeight: '1.5'
                        }}
                        rows={1}
                        placeholder={
                            selectedContact?.botActive
                                ? "Chat administrado por bot..."
                                : selectedAttachment
                                ? "Añadir un comentario al archivo..."
                                : "Escribe un mensaje..."
                        }
                        value={messageText}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDownWrapper}
                        onPaste={handlePaste}
                        disabled={isInputDisabled}
                        autoComplete="off"
                    />
                    <QuickRepliesModal
                        show={showQuickReplies}
                        searchQuery={quickReplySearch}
                        onSelect={handleQuickReplySelect}
                        onClose={handleQuickReplyClose}
                        onForceClose={handleQuickReplyForceClose}
                        inputRef={inputRef}
                    />
                </div>

                {/* Botón Enviar */}
                <div className="col-auto ms-2">
                    <button
                        id="whatsapp-send-btn"
                        type="button"
                        className="btn btn-primary rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0"
                        style={{
                            width: '48px',
                            height: '48px',
                            cursor: isSendDisabled ? 'not-allowed' : 'pointer',
                            opacity: isSendDisabled ? 0.65 : 1,
                            filter: isSendDisabled ? 'grayscale(100%)' : 'none'
                        }}
                        onClick={handleSendMessage}
                        disabled={isSendDisabled}
                        title={selectedContact?.botActive ? "Interacción deshabilitada por bot" : "Enviar mensaje"}
                    >
                        <i className="ri-send-plane-2-fill fs-20 text-white"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatInput;
